import React, { useCallback, useEffect, useState } from 'react';
import { CloudUpload, AlertTriangle, Loader2, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { listQueue, processQueue, removeItem, retryItem, OFFLINE_QUEUE_CHANGED } from '../lib/offlineQueue';

const STATUS_LABEL = { pending: 'In attesa di rete', conflict: 'Conflitto', error: 'Errore' };

const QueueItem = ({ item, onAction }) => (
  <div data-testid={`sync-item-${item.id}`} className="rounded-lg border border-slate-200 p-3 space-y-2">
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="font-medium text-slate-900 text-sm break-words">{item.label}</p>
        <p className="text-xs text-slate-500">{new Date(item.createdAt).toLocaleString('it-IT')}</p>
      </div>
      <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${item.status === 'pending' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>
        {STATUS_LABEL[item.status]}
      </span>
    </div>
    {item.status === 'conflict' && (
      <>
        <p className="text-xs text-red-700">
          Mentre eri offline {item.serverUpdatedBy || 'un altro utente'} ha modificato questo verbale. Scegli quale versione tenere.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" data-testid="sync-keep-mine-btn" onClick={() => onAction(item, 'mine')}>Usa la mia versione</Button>
          <Button size="sm" variant="outline" data-testid="sync-keep-server-btn" onClick={() => onAction(item, 'discard')}>Tieni quella sul server</Button>
        </div>
      </>
    )}
    {item.status === 'error' && (
      <>
        <p className="text-xs text-red-700">{item.errorMessage}</p>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" data-testid="sync-retry-btn" onClick={() => onAction(item, 'retry')}>
            <RefreshCw className="w-3 h-3 mr-1" /> Riprova
          </Button>
          <Button size="sm" variant="ghost" className="text-red-600" data-testid="sync-delete-btn" onClick={() => onAction(item, 'discard')}>
            <Trash2 className="w-3 h-3 mr-1" /> Elimina
          </Button>
        </div>
      </>
    )}
  </div>
);

export const PendingSyncPanel = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const refresh = useCallback(() => listQueue().then(setItems).catch(() => {}), []);

  useEffect(() => {
    const sync = () => processQueue().catch(() => {});
    const onSynced = (e) => toast.success(e.detail.sent === 1 ? '1 elemento salvato sul server' : `${e.detail.sent} elementi salvati sul server`);
    refresh();
    sync();
    window.addEventListener(OFFLINE_QUEUE_CHANGED, refresh);
    window.addEventListener('online', sync);
    window.addEventListener('offline-queue-synced', onSynced);
    const timer = setInterval(sync, 30000);
    return () => {
      window.removeEventListener(OFFLINE_QUEUE_CHANGED, refresh);
      window.removeEventListener('online', sync);
      window.removeEventListener('offline-queue-synced', onSynced);
      clearInterval(timer);
    };
  }, [refresh]);

  const onAction = async (item, action) => {
    if (action === 'discard') await removeItem(item.id);
    else await retryItem(item, action);
  };

  if (!items.length) return null;
  const problems = items.filter((i) => i.status !== 'pending').length;

  return (
    <>
      <button
        data-testid="pending-sync-btn"
        onClick={() => setOpen(true)}
        className={`fixed bottom-4 left-4 z-50 flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium text-white shadow-lg transition-transform hover:scale-105 ${problems ? 'bg-red-600' : 'bg-blue-700'}`}
      >
        {problems ? <AlertTriangle className="w-4 h-4" /> : navigator.onLine ? <Loader2 className="w-4 h-4 animate-spin" /> : <CloudUpload className="w-4 h-4" />}
        {problems ? `${problems} da controllare` : `${items.length} da inviare`}
      </button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-full sm:max-w-md overflow-y-auto" data-testid="pending-sync-panel">
          <SheetHeader>
            <SheetTitle>Salvati sul telefono</SheetTitle>
            <SheetDescription>Verranno inviati al server appena torna la connessione.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-3">
            {items.map((item) => <QueueItem key={item.id} item={item} onAction={onAction} />)}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default PendingSyncPanel;
